import React, { Component } from 'react';
import { Field } from 'redux-form';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import moment from 'moment';

import DayPicker from 'react-day-picker';
import 'react-day-picker/lib/style.css';
import './index.css';
import { isRequired } from 'common/form-validators';
import * as formatters from 'common/formatters';
import * as icons from 'common/svg/icons';

class renderField extends Component {
  constructor(props) {
    super(props);

    this.state = {
      isOpen: false,
      selected: null,
    };
  }

  componentDidUpdate(prevProps) {
    const { selected } = this.state;
    const {
      input: { value },
    } = this.props;
    // when value is initially pre-populated, manually select the calendar
    if (selected === null && value) {
      this.setState({ selected: new Date(value) });
    }
    this.updateDate(prevProps);
  }

  componentDidMount() {
    document.addEventListener('mousedown', this.handleOnBlur);
    this.updateDate(this.props);
  }

  updateDate = target => {
    const {
      input: { value },
      season,
      defaultToToday,
    } = this.props;

    let newdate = null;
    // on season change, set back to initial date or January 1st of the past/future season
    if (season !== target.season) {
      newdate =
        season === moment().year()
          ? new Date(this.initialDate)
          : new Date(season, 0);
      if (!this.initialDate) {
        this.initialDate = newdate;
      }
    }
    // if `defaultToToday` flag is true, default to today or other season's January 1st
    if (defaultToToday && !value) {
      newdate = season === moment().year() ? new Date() : new Date(season, 0);
    }

    if (newdate) {
      this.handleOnChange(newdate);
    }
  };

  // for blur on outside the component
  handleOnBlur = e => {
    if (
      this.calendar &&
      !this.calendar.contains(e.target) &&
      this.state.isOpen
    ) {
      this.setState({ isOpen: false });
    }
  };
  // for focus on input field
  handleOnFocus = () => {
    this.setState({ isOpen: true });
  };

  // for change on calendar
  handleOnChange = (selected, modifiers = {}) => {
    // when the disabled date is clicked
    if (modifiers.disabled) {
      return;
    }

    const {
      input: { onChange },
    } = this.props;
    if (onChange && typeof onChange === 'function') {
      onChange(selected);
    }

    this.setState({ isOpen: false, selected });
  };

  render() {
    const {
      input: { value, name },
      meta: { dirty, touched, valid, error },
      className,
      validText = null,
      validateOnChange = false,
      fromMonth,
      toMonth,
      disabledDays,
      season,
      ...rest
    } = this.props;

    // remove `defaultToToday` from rest to avoid error on Input
    const refinedRest = Object.keys(rest).reduce((result, key) => {
      if (key !== 'defaultToToday') {
        result[key] = rest[key];
      }
      return result;
    }, {});

    // In some cases we want to show validation errors immediately after the field
    // has been changed (vs. on blur)
    const touchedOrDirty = validateOnChange ? dirty : touched;

    // adding input class to reuse bulma
    const classes = classnames(className, 'input', {
      'is-danger': touchedOrDirty && error,
    });

    // We are initilizing "defaultMonth" for the DatePicker component to show upon first activation
    // Default is the current month
    // If we pass in the "season" prop and the season is not current year, it will show the Juanuary of that season
    let defaultMonth = moment().toDate();
    if (season && season !== moment().year()) {
      defaultMonth = moment(season, 'YYYY').toDate();
    }

    // If the DatePicker has set a value from user beofre, the default month will be the chosen month
    const initialMonth = this.state.selected
      ? moment(this.state.selected).toDate()
      : defaultMonth;

    return (
      <div className={'input__wrapper'}>
        <input
          type="text"
          id={name}
          name={name}
          value={value}
          className={classes}
          onFocus={this.handleOnFocus}
          {...refinedRest}
          autoComplete="off"
          readOnly
        />
        {touchedOrDirty && error && (
          <small className="text--error">{error}</small>
        )}
        {valid && validText && (
          <small className="text--primary">{validText}</small>
        )}
        <icons.calendar
          width="16px"
          height="14px"
          role="button"
          className={'datepicker__icon'}
        />

        <div
          ref={calendar => (this.calendar = calendar)}
          className={classnames(
            'datepicker',
            this.state.isOpen && 'datepicker--active'
          )}
        >
          <DayPicker
            month={initialMonth}
            selectedDays={this.state.selected}
            onDayClick={this.handleOnChange}
            fromMonth={fromMonth}
            toMonth={toMonth}
            disabledDays={disabledDays}
            fixedWeeks
          />
        </div>
      </div>
    );
  }
}

renderField.propTypes = {
  input: PropTypes.object.isRequired,
  meta: PropTypes.object.isRequired,
  className: PropTypes.string,
  validText: PropTypes.string,
  validateOnChange: PropTypes.bool,
  fromMonth: PropTypes.object,
  toMonth: PropTypes.object,
  disabledDays: PropTypes.array,
  defaultToToday: PropTypes.bool,
  season: PropTypes.number,
};
renderField.defaultProps = {
  disabledDays: [],
  defaultToToday: false,
};

const Datepicker = props => {
  const { component = renderField, title, validate, ...rest } = props;

  return (
    <fieldset className={'field'}>
      {title && (
        <label htmlFor={name}>
          {title}
          {isRequired(validate) && ' *'}
        </label>
      )}

      <Field
        {...rest}
        validate={validate}
        format={formatters.date}
        component={component}
      />
    </fieldset>
  );
};

Datepicker.propTypes = {
  component: PropTypes.func,
  className: PropTypes.string,
  title: PropTypes.string,
  name: PropTypes.string.isRequired,
  validate: PropTypes.arrayOf(PropTypes.func),
};

export default Datepicker;



// WEBPACK FOOTER //
// ./src/common/components/Datepicker/index.js